/**
 * Centralized Key Bindings Configuration
 * All game keybindings are defined here for easy modification
 */

export interface KeyBinding {
  code: string;      // KeyboardEvent.code (e.g., 'KeyQ', 'Space')
  display: string;   // Display name for UI (e.g., 'Q', 'SPACE')
}

export const KEY_BINDINGS = {
  // Movement (handled by InputManager)
  MOVE_UP: { code: 'KeyW', display: 'W' },
  MOVE_DOWN: { code: 'KeyS', display: 'S' },
  MOVE_LEFT: { code: 'KeyA', display: 'A' },
  MOVE_RIGHT: { code: 'KeyD', display: 'D' },
  DODGE: { code: 'Space', display: 'SPACE' },

  // Potions
  POTION_HEALTH: { code: 'KeyQ', display: 'Q' },
  POTION_MANA: { code: 'KeyE', display: 'E' },

  // Skills (number keys)
  SKILL_1: { code: 'Digit1', display: '1' },
  SKILL_2: { code: 'Digit2', display: '2' },
  SKILL_3: { code: 'Digit3', display: '3' },
  SKILL_4: { code: 'Digit4', display: '4' },
  SKILL_5: { code: 'Digit5', display: '5' },
  SKILL_6: { code: 'Digit6', display: '6' },

  // UI Menus
  MENU_INVENTORY: { code: 'KeyI', display: 'I' },
  MENU_CRAFTING: { code: 'KeyC', display: 'C' },
  MENU_QUESTS: { code: 'KeyJ', display: 'J' },
  MENU_MAP: { code: 'KeyM', display: 'M' },
  MENU_SKILLS: { code: 'KeyK', display: 'K' },
  MENU_PASSIVES: { code: 'KeyP', display: 'P' },
  MENU_TOGGLE: { code: 'Escape', display: 'ESC' },
  MINIMAP_TOGGLE: { code: 'Tab', display: 'TAB' },

  // Actions
  INTERACT: { code: 'KeyF', display: 'F' },
  TOWN_PORTAL: { code: 'KeyT', display: 'T' },

  // Loot
  LOOT_FILTER: { code: 'KeyZ', display: 'Z' },

  // System
  TOGGLE_LANGUAGE: { code: 'KeyL', display: 'L' },
  TOGGLE_MUTE: { code: 'KeyN', display: 'N' },
  QUICK_SAVE: { code: 'F5', display: 'F5' },
  QUICK_LOAD: { code: 'F9', display: 'F9' },
  SAVE_MENU: { code: 'F6', display: 'F6' },
  LOAD_MENU: { code: 'F7', display: 'F7' },
} as const;

// Type for key binding names
export type KeyBindingName = keyof typeof KEY_BINDINGS;

// Helper function to check if a key event matches a binding
export function isKeyPressed(event: KeyboardEvent, binding: KeyBinding): boolean {
  return event.code === binding.code;
}

// Helper function to get binding by code
export function getBindingByCode(code: string): KeyBindingName | null {
  for (const [name, binding] of Object.entries(KEY_BINDINGS)) {
    if (binding.code === code) {
      return name as KeyBindingName;
    }
  }
  return null;
}
